import { supabase } from '../lib/supabase';
import { toast } from 'react-toastify';

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.subscription = null;
  }

  // Request browser notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Show browser notification
  showBrowserNotification(title, options = {}) {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });

      notification.onclick = () => {
        window.focus();
        if (options?.onClick) {
          options?.onClick();
        }
        notification?.close();
      };

      return notification;
    }
  }

  // Show toast notification
  showToast(message, type = 'info', options = {}) {
    const toastOptions = {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    };

    switch (type) {
      case 'success':
        toast?.success(message, toastOptions);
        break;
      case 'error':
        toast?.error(message, toastOptions);
        break;
      case 'warning':
        toast?.warning(message, toastOptions);
        break;
      case 'info':
      default:
        toast?.info(message, toastOptions);
        break;
    }
  }

  // Create notification in database
  async createNotification(userId, notification) {
    try {
      const { data, error } = await supabase?.from('notifications')?.insert({
          user_id: userId,
          title: notification?.title,
          message: notification?.message,
          type: notification?.type || 'info',
          action_url: notification?.actionUrl,
          metadata: notification?.metadata || {}
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }
  }

  // Get user notifications
  async getUserNotifications(userId, options = {}) {
    try {
      const { limit = 50, offset = 0, unreadOnly = false } = options;

      let query = supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      if (unreadOnly) {
        query = query?.eq('is_read', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { data: null, error };
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { data: null, error };
    }
  }

  // Mark all notifications as read
  async markAllAsRead(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('user_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { data: null, error };
    }
  }

  // Delete notification
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { error };
    }
  }

  // Get unread count
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return { count, error: null };
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return { count: 0, error };
    }
  }

  // Subscribe to real-time notifications
  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel(`notifications:${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const notification = payload?.new;
          
          // Show browser notification
          this.showBrowserNotification(notification?.title, {
            body: notification?.message,
            tag: notification?.id,
            data: notification
          });

          // Show toast notification
          this.showToast(notification?.message, notification?.type);

          // Call callback with new notification
          if (callback) {
            callback(notification);
          }
        }
      )?.subscribe();

    this.subscription = channel;
    return channel;
  }

  // Unsubscribe from real-time notifications
  unsubscribeFromNotifications() {
    if (this.subscription) {
      supabase?.removeChannel(this.subscription);
      this.subscription = null;
    }
  }

  // Send notification to user (admin/system use)
  async sendNotification(userId, notification) {
    const { data, error } = await this.createNotification(userId, notification);
    
    if (!error && data) {
      // Notification will be delivered via real-time subscription
      return { success: true, data };
    }
    
    return { success: false, error };
  }

  // Bulk send notifications
  async sendBulkNotifications(userIds, notification) {
    try {
      const notifications = userIds?.map(userId => ({
        user_id: userId,
        title: notification?.title,
        message: notification?.message,
        type: notification?.type || 'info',
        action_url: notification?.actionUrl,
        metadata: notification?.metadata || {}
      }));

      const { data, error } = await supabase?.from('notifications')?.insert(notifications)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending bulk notifications:', error);
      return { data: null, error };
    }
  }
}

export default new NotificationService();